import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { usePost } from "../../hooks/usePost";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../store/ToastProvider";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { CompanyStatusHeader } from "../CompanyProfile/CompanyStatusheader";
import { greyText } from "../../styles";
import {
  BackgroundContainer,
  Card,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";

import { getPathwayFromInput } from "../../components/visuals/PathwayProgressBar";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { formatReadable } from "../../utils/formattingUtils";
import { MESSAGES } from "../../messages";
import {
  CompanySummary,
  CompanySummaryRequest,
} from "../../models/CompanyprofileModels";
import { ROUTES } from "../../routes/routeNames";
import { EngagementTrackerTable } from "./components/EngagementTrackerTable";
import {
  EngagementRecord,
  EngagementSubthemeMetric,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import { BadgeCards } from "./components/BadgeCards";
import { InlineLoadingPlaceholder } from "../../components/visuals/InlineLoadingPlaceholder";

export const CompanyEngagements = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [engagementRecords, setEngagementRecords] = useState<
    EngagementRecord[]
  >([]);
  const { companyId } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const {
    data: companySummaryData,
    sendData,
    isLoading,
    error: summaryDataError,
  } = usePost<CompanySummary, CompanySummaryRequest>();

  const {
    data,
    sendData: getEngagementData,
    isLoading: isEngagementDataLoading,
  } = usePost<EngagementRecord[], StewardshipFilterParam>();

  const {
    data: engagementSubThemeMetricsData,
    sendData: getEngagementSubThemeMetrics,
    isLoading: isEngagementSubThemeMetricsLoading,
  } = usePost<EngagementSubthemeMetric[], { compass_cid: string }>();

  const pathway = getPathwayFromInput(
    companySummaryData?.implicit_temperature || MESSAGES.NOT_AVAILABLE,
  );

  useEffect(() => {
    sendData("GET_COMPANY_SUMMARY", {
      compass_cid: companyId as string,
      company_name: companyName,
    });
  }, [companyId]);

  useEffect(() => {
    companyId &&
      getEngagementData("GET_ENGAGEMENT_DATA", {
        compass_cid: [companyId],
      });
    getEngagementSubThemeMetrics("GET_ENGAGEMENT_SUBTHEME_METRICS", {
      compass_cid: companyId as string,
    });
  }, [companyId]);

  useEffect(() => {
    if (companySummaryData) {
      setCompanyName(formatReadable(companySummaryData.name));
    }
  }, [companySummaryData]);

  useEffect(() => {
    if (data?.length) {
      const uniqueEngagementRecords = Array.from(
        new Map(data.map((item) => [item.engagement_record_id, item])).values(),
      );
      setEngagementRecords(uniqueEngagementRecords);
    }
  }, [data]);

  useEffect(() => {
    if (summaryDataError) {
      addToast(MESSAGES.SCREENING_TOOL.ERROR_FETCHING_COMPANY_SUMMARY, "error");
      navigate(ROUTES.SCREENING_TOOL);
    }
  }, [summaryDataError]);

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar
              address={
                companyName ? (
                  `company-engagements / ${companyName}`
                ) : (
                  <>
                    company-engagements /{" "}
                    <InlineLoadingPlaceholder label="Loading company name" />
                  </>
                )
              }
            />
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Company Engagements
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph ml={20} color={greyText}>
              {MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          <RowContainer ml={20} mt={1} ai="center">
            <CompanyStatusHeader
              companyId={companyId}
              companyName={companyName}
              pathway={pathway}
              data={companySummaryData}
              isLoading={isLoading}
            />
          </RowContainer>
        </NavBarContainer>
        <BadgeCards companyId={companyId || null} />
        <RowContainer ml={10} mt={1}>
          <Card
            isLoading={
              isEngagementDataLoading || isEngagementSubThemeMetricsLoading
            }
            w={100}
            h={"auto"}
            m="0 30px 0 10px"
          >
            <RowContainer mt={-1} jc="flex-start" ai="flex-start">
              <CompassParagraph fontSize={16} bold={true}>
                Engagements
              </CompassParagraph>
            </RowContainer>
            <EngagementTrackerTable
              companyId={companyId ?? ""}
              engagementRecords={engagementRecords}
              engagementSubThemeMetrics={engagementSubThemeMetricsData || []}
            />
          </Card>
        </RowContainer>
      </Column>
    </BackgroundContainer>
  );
};
